<script lang="ts" setup>
import { mdiMenu as mdiMenu, mdiMenuDown as mdiMenuArrow } from "@mdi/js";
import { computed, type Directive, h, reactive, withDirectives } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { VBadge } from "vuetify/components";
import { Tooltip } from "vuetify/directives";

import AppHelpDialog from "#components/dialogs/AppHelpDialog.vue";
import ReviewRubricDialog from "#components/dialogs/ReviewRubricDialog.vue";
import SeasonSelectionDialog from "#components/dialogs/SeasonSelectionDialog.vue";
import SeasonSearchDialog from "#components/dialogs/SeasonsSearchDialog.vue";
import LayoutStack from "#components/layout/LayoutStack.vue";
import { useAppTheme } from "#composables/use-app-theme";
import { useAuthAccount } from "#composables/use-auth-account";
import { useDialog } from "#composables/use-dialog";
import { appIcons } from "#composables/use-icons";
import { appConfig } from "#config/app";
import { useSeasonStore } from "#modules/season/season.store";
import type { Season } from "#modules/season/season.types";
import { routeNames } from "#src/router/constants";
import { createGetElId } from "#utilities/element";

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  (e: "toggle-drawer"): void;
}>();

/** Get HTML element ID (with shared component prefix) */
const getElId = createGetElId("the-app-bar");

const { dark, icon: themeIcon, toggleTheme } = useAppTheme();
const { mobile } = useDisplay();

const accountStore = reactive(useAuthAccount());
const seasonStore = useSeasonStore();

const logoutConfirmDialog = useDialog();
const reviewRubricDialog = useDialog();
const seasonSearchDialog = useDialog();
const seasonSelectionDialog = useDialog();
const helpDialog = useDialog();

const router = useRouter();

const handleLogout = () => {
  logoutConfirmDialog.hide();
  // Logout functionality is handled on logout page
  router.push({ name: routeNames.auth.logout });
};

const getIsSeasonCurrent = (season: Season) => season.id === seasonStore.currentSeason?.id;

/** Shared rendered season count badge (cannot use `h` directly in template!) */
const renderedSeasonCount = computed(() => {
  if (seasonStore.activeSeasons.length <= 1) return undefined;

  const otherActiveSeasonCount = seasonStore.currentSeason?.current
    ? seasonStore.activeSeasons.length - 1
    : seasonStore.activeSeasons.length;

  return withDirectives(
    h(VBadge, {
      class: "ml-n1",
      color: "warning",
      content: `+${otherActiveSeasonCount}`,
      inline: true,
    }),
    // NOTE: 'resolveDirective' does not work, as Vuetify directives do not appear to be registered globally?
    [[Tooltip as Directive, `${otherActiveSeasonCount} other active season(s)`]],
  );
});
</script>

<template>
  <VAppBar :color="!dark ? 'primary' : undefined" elevation="2">
    <template #prepend>
      <VBtn :active="false" icon :to="accountStore.authenticated ? '/dashboard' : '/'">
        <VAvatar class="pa-1" image="/logo-app-light.png" />
      </VBtn>
    </template>

    <template #title>
      <VAppBarTitle text="Composer Portal">
        <template v-if="accountStore.isComposer && !mobile">
          <VBtn class="ml-4" :to="{ name: routeNames.composer.submission.index }" variant="text">
            Submissions
          </VBtn>
        </template>
      </VAppBarTitle>
    </template>

    <template #append>
      <template v-if="appConfig.development && !mobile">
        <VBtn density="comfortable" href="http://localhost:8025/#" icon target="_blank">
          <template #default>
            <img
              alt="MailHog logo"
              class="opacity-60"
              src="/logo-mailhog.png"
              style="filter: brightness(0) invert(1)"
              width="24"
            />
          </template>
        </VBtn>
        <VBtn density="comfortable" :href="`${appConfig.links.api}/_/`" icon target="_blank">
          <template #default>
            <img alt="Pocketbase logo" class="opacity-60" src="/logo-pocketbase.png" width="20" />
          </template>
        </VBtn>
      </template>
      <LayoutStack v-if="seasonStore.currentSeason && !mobile" align-items="center" direction="row">
        <VIcon :icon="appIcons.mdiCalendar" />
        <span class="font-weight-medium">
          {{ seasonStore.currentSeason.name }}
          <VBadge
            v-if="!seasonStore.currentSeason.current"
            v-tooltip="'Temporary'"
            color="warning"
            dot
            inline
          />
        </span>
        <component :is="renderedSeasonCount" />
        <VMenu v-if="seasonStore.activeSeasons.length > 1" location="bottom right">
          <template #activator="{ props: activatorProps }">
            <VBtn
              v-bind="activatorProps"
              :id="getElId('season-menu-btn')"
              class="ml-n2"
              :icon="mdiMenuArrow"
              size="sm"
            />
          </template>
          <VList class="mt-1 py-0" density="comfortable" min-width="200">
            <VListSubheader title="Active Seasons" />
            <VDivider />
            <VListItem
              v-for="season in seasonStore.activeSeasons"
              :id="getElId('season-menu-item-season', season.id)"
              :key="season.id"
              :active="getIsSeasonCurrent(season)"
              :append-icon="getIsSeasonCurrent(season) ? appIcons.mdiCheckCircle : undefined"
              color="primary"
              @click="seasonStore.currentSeason = season"
            >
              <VListItemTitle :class="{ 'font-weight-bold': getIsSeasonCurrent(season) }">
                {{ season.name }}
              </VListItemTitle>
            </VListItem>
            <template v-if="accountStore.isCollaborator">
              <VDivider class="my-1" />
              <VListItem
                :id="getElId('season-menu-item-all')"
                :append-icon="appIcons.mdiSearch"
                title="All Seasons"
                @click="seasonSearchDialog.show"
              />
            </template>
          </VList>
        </VMenu>
      </LayoutStack>

      <VDivider
        v-if="!mobile && (appConfig.development || seasonStore.currentSeason)"
        class="my-4 mx-1"
        vertical
      />
      <template v-if="!mobile">
        <VBtn
          :id="getElId('theme-toggle-btn')"
          density="comfortable"
          :icon="themeIcon"
          @click="toggleTheme"
        />
        <VBtn
          :id="getElId('help-btn')"
          v-tooltip="'Help'"
          density="comfortable"
          :icon="appIcons.mdiHelp"
          @click="helpDialog.show"
        />
      </template>
      <template v-if="accountStore.account">
        <VMenu>
          <template #activator="{ props }">
            <VBtn :id="getElId('account-menu-btn')" v-bind="props" class="mx-1" icon>
              <VAvatar color="white">
                {{ accountStore.account.name.charAt(0).toUpperCase() }}
              </VAvatar>
            </VBtn>
          </template>
          <VList class="mt-1" min-width="200">
            <VListItem class="text-subtitle-2">{{ accountStore.account.name }}</VListItem>
            <VDivider />
            <template v-if="accountStore.isComposer">
              <VListItem exact :to="{ name: routeNames.composer.submission.index }">
                Submissions
              </VListItem>
              <VListItem @click="reviewRubricDialog.show">Review Rubric</VListItem>
            </template>
            <VListItem
              v-if="mobile && seasonStore.currentSeason && seasonStore.activeSeasons.length > 1"
              :id="getElId('account-menu-item-season')"
              @click="seasonSelectionDialog.show"
            >
              Change Season
              <template #append>
                <component :is="renderedSeasonCount" />
              </template>
            </VListItem>
            <VDivider v-if="accountStore.isComposer" />
            <VListItem to="/profile">Profile</VListItem>
            <VListItem @click="logoutConfirmDialog.show">Logout</VListItem>
          </VList>
        </VMenu>
      </template>
      <template v-else>
        <VBtn
          :id="getElId('actions-apply-btn')"
          :active="false"
          to="/auth/apply"
          variant="outlined"
        >
          Apply
        </VBtn>
        <VBtn
          :id="getElId('actions-login-btn')"
          :active="false"
          to="/auth/login"
          :variant="dark ? 'outlined' : 'elevated'"
        >
          Login
        </VBtn>
      </template>
      <VBtn
        v-if="mobile && accountStore.isCollaborator"
        :id="getElId('actions-drawer-toggle-btn')"
        density="comfortable"
        :icon="mdiMenu"
        @click="emit('toggle-drawer')"
      />
    </template>

    <ConfirmDialog
      v-model="logoutConfirmDialog.open.value"
      :close-on-confirm="false"
      title="Logout?"
      @confirm="handleLogout"
    >
      Are you sure you want to logout?
    </ConfirmDialog>

    <AppHelpDialog v-model="helpDialog.open.value" />
    <ReviewRubricDialog v-model="reviewRubricDialog.open.value" mode="image" />
    <SeasonSelectionDialog
      v-model="seasonSelectionDialog.open.value"
      :season-id="seasonStore.currentSeasonId"
      :seasons="seasonStore.activeSeasons"
      @select="(season) => (seasonStore.currentSeason = season)"
    />
    <SeasonSearchDialog
      v-model="seasonSearchDialog.open.value"
      :season-id="seasonStore.currentSeasonId"
      @select="(season) => (seasonStore.currentSeason = season)"
    >
      <template #footer="slotData">
        <VAlert
          v-if="!slotData.loading"
          class="mt-2 text-body-2"
          color="warning"
          density="compact"
          text="*Non-active seasons will not be kept across page refresh!"
        />
      </template>
    </SeasonSearchDialog>
  </VAppBar>
</template>

<style lang="scss" scoped>
:deep(.v-toolbar__append) {
  gap: spacing(2);
}

:deep(.v-app-bar-title > div) {
  display: flex;
  align-items: center;
}
</style>
